'use client';

// The /compare index below the masthead: a car filter, the three most-read
// matchups, then one shelf per segment.
//
// Filter rules (Dennis, 2026-10-17):
//   - Tiles are grouped by make (A to Z), models A to Z inside, on one grid so
//     every tile has the same width and columns line up.
//   - The hand-picked top cars show first; "+N more" opens the rest in place
//     and "Show top comparos" folds them back.
//   - Picking a car fades every tile except the cars it has a matchup with;
//     those light up in coral, and any hidden behind "more" appear in their
//     make group. Picking a lit car narrows the page to that one matchup.
//   - Clicking a selected car steps back one level; "Clear" resets.
//
// Everything is server-rendered with the filter at "all", so every card link
// is in the HTML.

import Link from 'next/link';
import { useMemo, useState } from 'react';
import { trackEvent } from '@/lib/analytics/counterscale';

export interface IndexCard {
  slug: string;
  nameA: string;
  nameB: string;
  personaA: string | null;
  personaB: string | null;
  teaser: string | null;
  reviewers: number;
  videos: number;
  segment: string;
  famA: string;
  famB: string;
  featureSrc: string | null;
  tileSrc: string | null;
  /** The same art with the cars swapped (b on the left). */
  tileSrcSwapped: string | null;
  /** Names printed over each car on the shelf art: model, and its generation
   *  code as a small tag ("GR Corolla" + "E210"). */
  artA: { name: string; gen: string };
  artB: { name: string; gen: string };
}

export interface FilterCar {
  fam: string;
  label: string;
  make: string;
  count: number;
  rivals: string[];
  /** Background for the tile: small 3q cut-out + its body box, if there is art. */
  art: {
    src: string;
    w: number;
    h: number;
    x0: number;
    y0: number;
    x1: number;
    y1: number;
  } | null;
}

interface Props {
  cards: IndexCard[];
  cars: FilterCar[];
  top: string[];
  mostRead: string[];
  segments: { key: string; label: string }[];
}

// Tile art box, CSS px. Kept in sync with .cix-tile-art in tokens.css.
const ART_W = 136;
const ART_H = 54;

function artStyle(art: NonNullable<FilterCar['art']>): React.CSSProperties {
  const bw = art.x1 - art.x0;
  const bh = art.y1 - art.y0;
  const ar = art.w / art.h;
  const imgW = Math.min((ART_W * 0.9) / bw, ((ART_H * 0.82) / bh) * ar);
  const imgH = imgW / ar;
  return {
    backgroundImage: `url(${art.src})`,
    backgroundSize: `${imgW.toFixed(1)}px ${imgH.toFixed(1)}px`,
    backgroundPosition: `center ${(ART_H * 0.92 - art.y1 * imgH).toFixed(1)}px`,
  };
}

export function CompareIndexBrowser({
  cards,
  cars,
  top,
  mostRead,
  segments,
}: Props) {
  const [pick, setPick] = useState('');
  const [second, setSecond] = useState('');
  const [expanded, setExpanded] = useState(false);

  const byFam = useMemo(() => new Map(cars.map((c) => [c.fam, c])), [cars]);
  const topSet = useMemo(
    () => new Set(top.filter((f) => byFam.has(f))),
    [top, byFam],
  );
  const picked = pick ? byFam.get(pick) : undefined;
  const rivals = useMemo(() => new Set(picked?.rivals ?? []), [picked]);

  const shown = cars.filter(
    (c) => expanded || topSet.has(c.fam) || c.fam === pick || rivals.has(c.fam),
  );
  const hiddenCount = cars.length - shown.length;
  const groups = new Map<string, FilterCar[]>();
  for (const c of [...shown].sort(
    (a, b) => a.make.localeCompare(b.make) || a.label.localeCompare(b.label),
  )) {
    const g = groups.get(c.make) ?? [];
    g.push(c);
    groups.set(c.make, g);
  }

  const onTile = (fam: string) => {
    if (!pick) {
      setPick(fam);
      trackEvent('compare_filter_car');
    } else if (fam === pick) {
      setPick(second);
      setSecond('');
    } else if (fam === second) {
      setSecond('');
    } else if (rivals.has(fam)) {
      setSecond(fam);
      trackEvent('compare_filter_pair');
    } else {
      setPick(fam);
      setSecond('');
      trackEvent('compare_filter_car');
    }
  };
  const clear = () => {
    setPick('');
    setSecond('');
  };

  const matches = (c: IndexCard) => {
    if (!pick) return true;
    const hasPick = c.famA === pick || c.famB === pick;
    if (!second) return hasPick;
    return hasPick && (c.famA === second || c.famB === second);
  };
  const featured = mostRead
    .map((s) => cards.find((c) => c.slug === s))
    .filter((c): c is IndexCard => !!c);
  const featuredSet = new Set(featured.map((c) => c.slug));

  let status: React.ReactNode = <i>Pick a car to see its matchups.</i>;
  if (picked && second) {
    status = (
      <>
        Showing{' '}
        <b>
          {picked.label} vs {byFam.get(second)?.label}
        </b>
        . Click either again to step back.
      </>
    );
  } else if (picked) {
    status = (
      <>
        <b>{picked.label}</b> has {picked.count} matchup
        {picked.count === 1 ? '' : 's'}. Pick a lit car to narrow to one.
      </>
    );
  }

  return (
    <>
      <section className="cix-filter" aria-label="Filter by car">
        <div className="cix-filter-label">
          <span>Filter by car</span>
          {pick && (
            <button type="button" className="cix-clear" onClick={clear}>
              Clear
            </button>
          )}
        </div>
        <div className="cix-grid">
          {Array.from(groups.entries()).flatMap(([make, list]) =>
            list.map((c, i) => {
              const isPick = c.fam === pick;
              const isRival = !!picked && rivals.has(c.fam) && !isPick;
              const cls = ['cix-cell'];
              if (i === list.length - 1) cls.push('last');
              if (picked && !isPick && !isRival) cls.push('faded');
              if (isRival) cls.push('rival');
              const tileCls = c.fam === second ? 'cix-tile second' : 'cix-tile';
              return (
                <div key={c.fam} className={cls.join(' ')}>
                  <span className="cix-make">{i === 0 ? make : ''}</span>
                  <button
                    type="button"
                    className={tileCls}
                    aria-pressed={isPick || c.fam === second}
                    onClick={() => onTile(c.fam)}
                  >
                    <span
                      className="cix-tile-art"
                      style={c.art ? artStyle(c.art) : undefined}
                    />
                    <span className="cix-tile-name">
                      {c.label} <small>{c.count}</small>
                    </span>
                  </button>
                </div>
              );
            }),
          )}
          {(hiddenCount > 0 || expanded) && (
            <div className="cix-cell plain">
              <span className="cix-make" />
              <button
                type="button"
                className="cix-tile cix-more"
                onClick={() => {
                  setExpanded(!expanded);
                  if (!expanded) trackEvent('compare_filter_more');
                }}
              >
                <span className="cix-tile-name">
                  {expanded ? 'Show top comparos' : `+${hiddenCount} more`}
                </span>
              </button>
            </div>
          )}
        </div>
        <p className="cix-status" aria-live="polite">
          {status}
        </p>
      </section>

      <hr className="compare-index-rule" />

      {!pick && featured.length > 0 && (
        <section className="cix-most-read">
          <h2 className="cix-shelf-label">Most read</h2>
          <div className="cix-featured">
            {featured.map((c, i) => (
              <IndexCardLink key={c.slug} card={c} featured rank={i + 1} />
            ))}
          </div>
        </section>
      )}

      {segments.map((s) => {
        const list = cards.filter(
          (c) =>
            c.segment === s.key &&
            matches(c) &&
            (pick !== '' || !featuredSet.has(c.slug)),
        );
        if (!list.length) return null;
        return (
          <section key={s.key}>
            <h2 className="cix-shelf-label">
              {s.label} <span>{list.length}</span>
            </h2>
            <div className={second ? 'cix-featured' : 'cix-shelf'}>
              {list.map((c) => (
                <IndexCardLink
                  key={c.slug}
                  card={c}
                  featured={!!second}
                  pickedOnRight={!!pick && c.famB === pick && c.famA !== pick}
                />
              ))}
            </div>
          </section>
        );
      })}
    </>
  );
}

function IndexCardLink({
  card,
  featured,
  rank,
  pickedOnRight,
}: {
  card: IndexCard;
  featured?: boolean;
  rank?: number;
  /** The filtered car is car b: swap so it sits on the left. */
  pickedOnRight?: boolean;
}) {
  if (featured) {
    return (
      <Link href={`/compare/${card.slug}`} className="cix-card">
        <span className="cix-card-art feature">
          {card.featureSrc && (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={card.featureSrc} alt="" loading="lazy" decoding="async" />
          )}
        </span>
        <span className="cix-card-body">
          <span className="cix-card-title">
            {rank != null && <span className="cix-rank">{rank}</span>}
            {card.nameA} <i>vs</i> {card.nameB}
          </span>
          {(card.personaA || card.personaB) && (
            <span className="cix-card-persona">
              {[card.personaA, card.personaB].filter(Boolean).join(' · ')}
            </span>
          )}
          {card.teaser && (
            <span className="cix-card-teaser">“{card.teaser}”</span>
          )}
          <span className="cix-card-meta">See the matchup →</span>
        </span>
      </Link>
    );
  }
  const left = pickedOnRight ? card.artB : card.artA;
  const right = pickedOnRight ? card.artA : card.artB;
  const src = pickedOnRight ? card.tileSrcSwapped : card.tileSrc;
  const personas = pickedOnRight
    ? [card.personaB, card.personaA]
    : [card.personaA, card.personaB];
  return (
    <Link
      href={`/compare/${card.slug}`}
      className="cix-card"
      aria-label={`${card.nameA} versus ${card.nameB}`}
    >
      <span className="cix-card-art tile">
        {src && (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={src} alt="" loading="lazy" decoding="async" />
        )}
        <span className="cix-art-name l" aria-hidden="true">
          {left.name}
          {left.gen && <small>{left.gen}</small>}
        </span>
        <span className="cix-art-name r" aria-hidden="true">
          {right.name}
          {right.gen && <small>{right.gen}</small>}
        </span>
        <span className="cix-vs" aria-hidden="true">
          vs
        </span>
      </span>
      <span className="cix-card-body tight">
        {personas.some(Boolean) && (
          <span className="cix-card-persona">
            {personas.filter(Boolean).join(' · ')}
          </span>
        )}
        <span className="cix-card-meta">
          <b>{card.reviewers}</b> reviewers · {card.videos} videos
        </span>
      </span>
    </Link>
  );
}
